import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Globe, WarningCircle, CalendarBlank } from "@phosphor-icons/react";
import { PeriodPresets } from "./PeriodPresets";
import { FilingPeriodList } from "./FilingPeriodList";
import { DateInput } from "./DateInput";
import { InfoHint } from "./InfoHint";
import { BlockCard } from "./BlockCard";
import { PRESETS } from "../workflow/periodPresets";
import { frequencyMismatches, filedOnOtherPeriods, periodDayCount } from "../lib/clientFrequency";

/**
 * v0.4.37 R5 + CL3 compliance revision (2026-06-04).
 *
 * Date inputs are ALWAYS editable (no readOnly, no greyed-out
 * state). The R-PERIOD readOnly behaviour shipped earlier this
 * session was rejected by the operator and reverted in favour of
 * auto-detect-from-ingest-runs (CL3A) — which makes the readOnly
 * unnecessary.
 *
 * Active preset row also shows a `CUSTOM` chip when the active
 * range matches no preset (CL3B). The amber out-of-range warning
 * now reads against the auto-detected baseline (CL3E).
 */
export const FilingPeriodCard = ({
  periodStart, setPeriodStart, periodEnd, setPeriodEnd,
  countryFilter, setCountryFilter, countryOptions,
  selectedRuns, selected,
  autoDetectedRange,
  client,
  step = null, accent = null,   // A2-467
}) => {
  const { t } = useTranslation();

  // v0.6.8 — the client's declared filing frequency (Settings → VAT
  // registrations → Frequency) was saved and shown but never checked
  // against the period actually run here — operator flagged this as a
  // missing correlation. Per operator instruction, the declared
  // frequency is the authoritative reference: every mismatch warns
  // (not just a monthly filer running a longer period), one row per
  // registration whose declared cadence doesn't match the selected
  // period's actual length. Non-blocking — matches the existing
  // auto-detected-range warning below.
  const mismatches = useMemo(
    () => frequencyMismatches(client, periodStart, periodEnd),
    [client, periodStart, periodEnd],
  );
  const otherPeriods = useMemo(
    () => filedOnOtherPeriods(client, periodStart, periodEnd),
    [client, periodStart, periodEnd],
  );

  // Upfront period validation — surfaced immediately in this section
  // rather than only at Run-calculation time, so operators don't waste
  // effort entering FX rates against an invalid period.
  const missingDate = !periodStart || !periodEnd;
  const startAfterEnd = !!periodStart && !!periodEnd && periodStart > periodEnd;
  const periodInvalid = missingDate || startAfterEnd;

  // CL3B — is any preset active? The PeriodPresets component
  // already highlights matching keys via its own internal
  // comparison; we render a CUSTOM chip when none match.
  const presetActive = useMemo(() => {
    for (const p of PRESETS) {
      const r = p.fn ? p.fn() : null;
      if (r && r.start === periodStart && r.end === periodEnd) return p.key;
    }
    return null;
  }, [periodStart, periodEnd]);

  // CL3E — amber compliance warning. Surfaces only when the
  // operator has MANUALLY changed the period away from the
  // auto-detected baseline.
  const manuallyChanged = autoDetectedRange
    && (autoDetectedRange.start !== periodStart
        || autoDetectedRange.end !== periodEnd);
  const excludedRunCount = useMemo(() => {
    if (!manuallyChanged) return 0;
    let n = 0;
    for (const r of selectedRuns) {
      const dr = r.date_range || {};
      const minD = dr.min || dr.first_tx_date;
      const maxD = dr.max || dr.last_tx_date;
      if (minD && minD < periodStart) { n += 1; continue; }
      if (maxD && maxD > periodEnd) n += 1;
    }
    return n;
  }, [selectedRuns, periodStart, periodEnd, manuallyChanged]);

  return (
    <BlockCard
      testid="filing-period-card" step={step} accent={accent}
      icon={CalendarBlank}
      title={t("ui.calculate.period_block_title", "Which period to calculate")}
      purpose={t("ui.calculate.period_block_purpose",
                 "Pick a quick period, choose a filing period, or set the dates yourself")}
    >
      <div className="space-y-4">
      <div className="flex items-center justify-end">
        {!presetActive && (
          <span
            data-testid="filing-period-custom-chip"
            className="text-[10px] font-semibold bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded-full"
          >{t("ui.calculate.period_card.custom" , { defaultValue: "Custom" })}</span>
        )}
        {autoDetectedRange && !manuallyChanged && selectedRuns.length > 0 && (
          <span
            data-testid="filing-period-autodetected-badge"
            className="text-[10px] font-semibold bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700 px-2 py-1 rounded-full"
          >{t("ui.calculate.period_card.autodetected", { defaultValue: "Auto-detected from data" })}</span>
        )}
      </div>
      <PeriodPresets
        presets={PRESETS}
        periodStart={periodStart}
        periodEnd={periodEnd}
        onSelect={({ start, end }) => {
          setPeriodStart(start); setPeriodEnd(end);
        }}
      />
      {periodInvalid && (
        <div
          data-testid="filing-period-invalid-warning"
          className="border border-red-300 dark:border-red-800 bg-red-500/10 dark:bg-red-900/40 rounded-xl px-4 py-3 text-[11px] text-red-700 dark:text-red-300 flex items-start gap-2"
        >
          <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>
            {missingDate
              ? t("ui.calculate.period_card.missing_date", { defaultValue: "Set both a start and end date for the filing period before continuing. Exchange-rate entry below is derived from this period." })
              : t("ui.calculate.period_card.start_after_end", { start: periodStart, end: periodEnd, defaultValue: `Period start (${periodStart}) is after period end (${periodEnd}). Fix the dates before entering exchange rates or running the calculation.` })}
          </span>
        </div>
      )}
      <hr className="border-slate-200 dark:border-slate-700" />
      {/* v0.6.10 — was a 3-column grid of near-empty panels (the two
          date fields occupied ~30% of their own boxes) plus a
          full-width country row beneath. Now two columns: the period
          list on the left, and the manual range + country filter
          stacked on the right, so the section is roughly half the
          height with more in it. */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="surface-panel p-4 rounded-xl md:col-span-3">
          <div className="text-xs font-semibold text-teal-700 dark:text-teal-300 mb-2">
            {t("ui.calculate.period_card.filing_periods", { defaultValue: "Filing periods" })}
          </div>
          <FilingPeriodList
            client={client}
            periodStart={periodStart}
            periodEnd={periodEnd}
            dataRange={autoDetectedRange}
            onSelect={({ start, end }) => {
              setPeriodStart(start); setPeriodEnd(end);
            }}
          />
        </div>
        <div className="md:col-span-2 space-y-4">
          <div className="surface-panel p-4 rounded-xl">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">
              {t("ui.calculate.period_card.set_range", { defaultValue: "Or set the range directly" })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-[10px] text-slate-500 dark:text-slate-400 mb-1">
                  {t("ui.calculate.period_start")}
                </span>
                <DateInput
                  testid="calc-period-start"
                  aria-label={t("ui.calculate.period_start")}
                  value={periodStart}
                  onChange={setPeriodStart}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-2 py-2 tabular-nums text-xs rounded-xl"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] text-slate-500 dark:text-slate-400 mb-1">
                  {t("ui.calculate.period_end")}
                </span>
                <DateInput
                  testid="calc-period-end"
                  aria-label={t("ui.calculate.period_end")}
                  value={periodEnd}
                  onChange={setPeriodEnd}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-2 py-2 tabular-nums text-xs rounded-xl"
                />
              </label>
            </div>
            {!periodInvalid && (
              <div
                data-testid="filing-period-length"
                className="tabular-nums text-[10px] text-slate-500 dark:text-slate-400 mt-2"
              >
                {t("ui.calculate.period_card.length_days", { days: periodDayCount(periodStart, periodEnd), defaultValue: `${periodDayCount(periodStart, periodEnd)} days` })}
              </div>
            )}
          </div>
          <div className="surface-panel p-4 rounded-xl">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
              <Globe size={12} weight="bold" />
              {t("ui.calculate.country_filter_label")}
              <InfoHint
                testid="country-filter-info"
                label={t("ui.calculate.country_filter_subtitle")}
              />
            </div>
            <select
              data-testid="country-filter"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-3 py-2 text-sm rounded-xl"
            >
              {countryOptions.map((c) => (
                <option key={c} value={c}>
                  {c === "ALL" ? t("ui.calculate.country_filter_all") : c}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {manuallyChanged && excludedRunCount > 0 && selected.length > 0 && (
        <div
          data-testid="filing-period-out-of-range-warning"
          className="border border-amber-200 dark:border-amber-800 bg-amber-500/10 dark:bg-amber-900/40 rounded-xl px-4 py-3 text-[11px] text-amber-700 dark:text-amber-300"
        >
          ⚠ {t("ui.calculate.period_card.out_of_range", { detected_start: autoDetectedRange.start, detected_end: autoDetectedRange.end, start: periodStart, end: periodEnd, count: excludedRunCount, defaultValue: `Auto-detected period from your data: ${autoDetectedRange.start} → ${autoDetectedRange.end}. You have manually changed this to ${periodStart} → ${periodEnd} — ${excludedRunCount} run(s) will be excluded.` })}
        </div>
      )}
      {!periodInvalid && mismatches.length > 0 && (
        <div
          data-testid="filing-period-frequency-mismatch-warning"
          className="border border-amber-200 dark:border-amber-800 bg-amber-500/10 dark:bg-amber-900/40 rounded-xl px-4 py-3 text-[11px] text-amber-700 dark:text-amber-300"
        >
          ⚠ {t("ui.calculate.period_card.mismatch_before", { start: periodStart, end: periodEnd, defaultValue: `This period (${periodStart} → ${periodEnd}) doesn't match the declared filing frequency for:` })}{" "}
          {mismatches.map((m, i) => (
            <span key={m.label}>
              {i > 0 && ", "}
              {m.label} ({m.declared.charAt(0) + m.declared.slice(1).toLowerCase()})
            </span>
          ))}
          {t("ui.calculate.period_card.mismatch_after", { defaultValue: ". Set the frequency in Settings if that's changed, or confirm this period is intentional." })}
        </div>
      )}
      {!periodInvalid && otherPeriods.length > 0 && (
        <div data-testid="filing-period-other-cadences-note"
          className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 rounded-xl px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
          {otherPeriods.map((o) => (
            <p key={o.cadence}>
              {t("ui.calculate.period_card.other_cadence", {
                labels: o.labels.join(", "),
                cadence: t(`ui.calculate.period_card.cadence_${o.cadence.toLowerCase()}`, { defaultValue: o.cadence.toLowerCase().replace("_", "-") }),
                defaultValue: `${o.labels.join(", ")}: filed ${o.cadence.toLowerCase().replace("_", "-")}. Run a separate calculation for each of those periods to file them; their forms in this calculation are not for filing.`,
              })}
            </p>
          ))}
        </div>
      )}
      </div>
    </BlockCard>
  );
};
